import { React, CheckIcon, ChevronDownIcon } from '../vendor/runtime.js';

// A styled single-select menu. The popover layer keeps menus above clipped filter panels.
export function FilterSelect({ children, value, onChange, className = '', label, name, disabled, id, invalid, describedBy }) {
  const options = React.Children.toArray(children).filter(child => child?.type === 'option');
  const selected = options.find(option => String(option.props.value) === String(value));
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const trigger = React.useRef(null);
  const menu = React.useRef(null);
  const uid = React.useId();
  const menuId = `${uid}-options`;
  const accessibleLabel = label || name || 'Filter';
  const close = (restore = false) => {
    menu.current?.hidePopover?.();
    setOpen(false);
    if (restore) trigger.current?.focus();
  };
  const choose = index => {
    const option = options[index];
    if (!option || option.props.disabled) return;
    onChange?.({ target: { value: option.props.value, name }, currentTarget: { value: option.props.value, name } });
    close(true);
  };
  React.useEffect(() => {
    if (open && active >= options.length) setActive(0);
  }, [options.length, active, open]);
  React.useLayoutEffect(() => {
    if (!open) return;
    const node = menu.current;
    const position = () => {
      const rect = trigger.current.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const upward = below < 220 && above > below;
      Object.assign(node.style, {
        width: `${Math.min(Math.max(rect.width, 190), window.innerWidth - 24)}px`,
        left: `${Math.max(12, Math.min(rect.left, window.innerWidth - Math.max(rect.width, 190) - 12))}px`,
        top: upward ? 'auto' : `${rect.bottom + 8}px`,
        bottom: upward ? `${window.innerHeight - rect.top + 8}px` : 'auto',
        maxHeight: `${Math.max(100, Math.min(300, upward ? above : below))}px`,
      });
    };
    position();
    node.showPopover?.();
    node.focus();
    const outside = event => {
      if (!node.contains(event.target) && !trigger.current.contains(event.target)) close();
    };
    const scroll = event => { if (!node.contains(event.target)) position(); };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [open]);
  React.useEffect(() => {
    if (open) document.getElementById(`${uid}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);
  const show = () => {
    setActive(Math.max(0, options.findIndex(option => String(option.props.value) === String(value))));
    setOpen(true);
  };
  return <div className={`cb-select ${className.includes('flex-1') ? 'cb-select-grow' : ''}`}>
    <button ref={trigger} id={id} type="button" disabled={disabled}
      className={`cb-select-trigger ${className.includes('bg-slate-700') ? 'cb-select-dark' : ''}`}
      aria-label={`${accessibleLabel}: ${selected?.props.children || 'Select'}`} aria-haspopup="listbox"
      aria-expanded={open} aria-controls={open ? menuId : undefined}
      aria-invalid={invalid || undefined} aria-describedby={describedBy}
      onClick={() => open ? close() : show()}
      onKeyDown={event => { if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); show(); } }}>
      <span>{selected?.props.children || 'Select'}</span><ChevronDownIcon aria-hidden="true" />
    </button>
    {open && <div ref={menu} popover="manual" id={menuId} className="cb-select-menu" role="listbox"
      aria-label={accessibleLabel} tabIndex={-1} aria-activedescendant={`${uid}-${active}`}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); }
        else if (event.key === 'Tab') { close(); trigger.current?.focus(); }
        else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(active); }
        else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          setActive(index => event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
        } else if (event.key.length === 1) {
          const match = options.findIndex(option => String(option.props.children).toLowerCase().startsWith(event.key.toLowerCase()));
          if (match >= 0) setActive(match);
        }
      }}>
      <div className="cb-select-caption">{accessibleLabel}</div>
      {options.map((option, index) => <div key={option.key || index} id={`${uid}-${index}`} role="option"
        aria-selected={String(option.props.value) === String(value)} aria-disabled={option.props.disabled || undefined}
        className={`cb-select-option ${active === index ? 'is-active' : ''}`}
        onPointerMove={() => setActive(index)} onClick={() => choose(index)}>
        <span>{option.props.children}</span>{String(option.props.value) === String(value) && <CheckIcon aria-hidden="true" />}
      </div>)}
    </div>}
  </div>;
}
